import React, { useEffect, useState } from "react";
import axios from "axios";
import { startImpersonation } from "../auth/session";

interface Props {
  authToken: string;
}

interface Candidate {
  id: string;
  username: string;
  displayName: string;
  userType: "user" | "premium" | "vip" | "admin";
}

interface StartResponse {
  access_token: string;
  expiresAt: string;
  readOnly: boolean;
  target: { id: string; username: string; displayName: string };
}

/** Admin panel: search a user and view the dashboard as them (read-only). */
const ImpersonateUser: React.FC<Props> = ({ authToken }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Candidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    // Each keystroke aborts the previous search, so an older response can
    // never land after a newer one and overwrite its results.
    const controller = new AbortController();
    const handle = window.setTimeout(async () => {
      try {
        const res = await axios.get<Candidate[]>(
          "/api/admin/impersonation/users",
          {
            params: { search: search || undefined },
            headers: { Authorization: `Bearer ${authToken}` },
            signal: controller.signal,
          },
        );
        if (controller.signal.aborted) return;
        setResults(res.data);
        setError(null);
      } catch {
        if (controller.signal.aborted) return;
        setError("Failed to search users");
      }
    }, 300);
    return () => {
      window.clearTimeout(handle);
      controller.abort();
    };
  }, [search, authToken]);

  const impersonate = async (user: Candidate) => {
    if (
      !window.confirm(
        `View the dashboard as @${user.username} (read-only, 30 minutes)? This is audit-logged.`,
      )
    ) {
      return;
    }
    setBusyId(user.id);
    try {
      const res = await axios.post<StartResponse>(
        `/api/admin/impersonation/${user.id}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      startImpersonation(res.data.access_token, {
        username: res.data.target.username,
        displayName: res.data.target.displayName,
        expiresAt: res.data.expiresAt,
        readOnly: res.data.readOnly,
      });
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? err.response?.data?.message
        : undefined;
      setError(message || "Failed to start impersonation");
      setBusyId(null);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#2d3748",
        borderRadius: "12px",
        padding: "24px",
        border: "1px solid #4a5568",
        marginBottom: "24px",
      }}
    >
      <h4
        style={{
          fontSize: "18px",
          fontWeight: "600",
          color: "#e2e8f0",
          marginBottom: "8px",
        }}
      >
        Impersonate User
      </h4>
      <p style={{ color: "#a0aec0", fontSize: "13px", marginTop: 0 }}>
        View the dashboard as another user to troubleshoot their settings.
        Read-only, expires after 30 minutes, and every session is audit-logged.
        Admin accounts cannot be impersonated.
      </p>
      <input
        type="text"
        placeholder="Search by username…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%",
          maxWidth: "360px",
          padding: "8px 12px",
          borderRadius: "6px",
          border: "1px solid #4a5568",
          backgroundColor: "#1a202c",
          color: "#e2e8f0",
          marginBottom: "12px",
        }}
      />
      {error && <p style={{ color: "#fc8181", margin: "0 0 12px" }}>{error}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {results.map((u) => (
          <div
            key={u.id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 0",
              borderBottom: "1px solid #4a5568",
              color: "#e2e8f0",
            }}
          >
            <span>
              {u.displayName || u.username}{" "}
              <span style={{ color: "#a0aec0" }}>
                @{u.username} · {u.userType}
              </span>
            </span>
            <button
              disabled={u.userType === "admin" || busyId !== null}
              title={
                u.userType === "admin"
                  ? "Admins can't be impersonated"
                  : undefined
              }
              onClick={() => impersonate(u)}
              style={{
                backgroundColor:
                  u.userType === "admin" ? "#4a5568" : "rgba(220,38,38,0.2)",
                color: u.userType === "admin" ? "#a0aec0" : "#fc8181",
                border: "1px solid #dc2626",
                borderRadius: "6px",
                padding: "4px 12px",
                cursor: u.userType === "admin" ? "not-allowed" : "pointer",
              }}
            >
              {busyId === u.id ? "Starting…" : "View as"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImpersonateUser;
